import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { serviceRequestService } from '../../services/serviceRequestService';
import { useGuestStay } from '../../hooks/useGuestStay';
import { ApiError, BookingDetails, ServiceRequest } from '../../types';
import { GDTop, GDDone } from './GDBits';

interface HousekeepingScreenProps {
  booking: BookingDetails;
}

const CHIP_KEYS = [
  'towels', 'cleaning', 'toiletries', 'water', 'pillow', 'laundry', 'broken', 'dnd',
] as const;

/**
 * One-tap housekeeping requests: pick what you need, say when, send.
 * "Something's broken" routes as a maintenance request at high priority.
 */
const HousekeepingScreen: React.FC<HousekeepingScreenProps> = ({ booking }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canRequestServices, isCheckedIn, hotelInfo } = useGuestStay(booking);
  // Per-hotel toggle (admin "Guest App" page); absent = shown. Hidden means
  // every request goes out as "as soon as possible".
  const showWhen = hotelInfo?.hotel?.features?.housekeeping_when !== false;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [when, setWhen] = useState<'asap' | 'hour' | 'evening'>('asap');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ServiceRequest | null>(null);

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const submit = async () => {
    const items = Array.from(selected).map(key => t(`gd.hk.chips.${key}`));
    const description = [items.join(', '), note.trim()].filter(Boolean).join('. ');
    if (!description) {
      setError(t('gd.hk.emptyError'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const requestedTime = (() => {
        if (when === 'hour') return new Date(Date.now() + 60 * 60 * 1000).toISOString();
        if (when === 'evening') {
          const evening = new Date();
          evening.setHours(18, 0, 0, 0);
          if (evening.getTime() < Date.now()) evening.setTime(Date.now() + 60 * 60 * 1000);
          return evening.toISOString();
        }
        return undefined;
      })();
      const broken = selected.has('broken');
      const response = await serviceRequestService.createServiceRequest({
        type: broken ? 'maintenance' : 'housekeeping',
        description,
        priority: broken ? 'high' : 'medium',
        ...(requestedTime ? { requested_time: requestedTime } : {}),
      });
      const created: any = response.data;
      setResult((created && created.data) || created);
    } catch (err) {
      setError((err as ApiError).error || t('gd.checkin.genericError'));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <section className="gd-screen">
        <GDDone title={t('gd.hk.sentTitle')}>
          <p>{t('gd.hk.sentBody')}</p>
          <span className="gd-pill gd-orange">
            {result.id
              ? t('gd.hk.sentPill', { id: result.id })
              : t('gd.hk.sentPillNoId')}
          </span>
          <button type="button" className="gd-btn" onClick={() => navigate('/home')}>
            {t('gd.common.backHome')}
          </button>
        </GDDone>
      </section>
    );
  }

  return (
    <section className="gd-screen">
      <GDTop title={hotelInfo?.hotel?.button_labels?.['housekeeping'] || t('gd.hk.title')} backTo="/home" />
      <div className="gd-body">
        {!isCheckedIn && !canRequestServices && (
          <div className="gd-note">
            {t('gd.hk.afterCheckin')}{' '}
            <button
              type="button"
              onClick={() => navigate('/checkin')}
              style={{
                font: 'inherit', fontWeight: 700, color: 'var(--gd-blue)',
                background: 'none', border: 0, cursor: 'pointer', padding: 0,
              }}
            >
              {t('gd.home.checkinTitle')}
            </button>
          </div>
        )}
        <p className="gd-muted">
          {booking.room_number
            ? t('gd.hk.introRoom', { room: booking.room_number })
            : t('gd.hk.intro')}
        </p>
        <div className="gd-chips">
          {CHIP_KEYS.map(key => (
            <button
              key={key}
              type="button"
              className={`gd-chip${selected.has(key) ? ' gd-on' : ''}`}
              onClick={() => toggle(key)}
            >
              {t(`gd.hk.chips.${key}`)}
            </button>
          ))}
        </div>
        <div className="gd-field">
          <label>{t('gd.hk.noteLabel')}</label>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder={t('gd.hk.notePlaceholder')} />
        </div>
        {showWhen && (
          <div className="gd-field">
            <label>{t('gd.hk.whenLabel')}</label>
            <div className="gd-chips">
              {(['asap', 'hour', 'evening'] as const).map(key => (
                <button
                  key={key}
                  type="button"
                  className={`gd-chip${when === key ? ' gd-on' : ''}`}
                  onClick={() => setWhen(key)}
                >
                  {t(`gd.hk.when.${key}`)}
                </button>
              ))}
            </div>
          </div>
        )}
        {error && (
          <div className="gd-note" style={{ background: 'var(--gd-red-soft)', color: 'var(--gd-red)' }}>
            {error}
          </div>
        )}
        <button type="button" className="gd-btn" disabled={busy} onClick={submit}>
          {busy ? t('gd.common.working') : t('gd.hk.sendButton')}
        </button>
      </div>
    </section>
  );
};

export default HousekeepingScreen;
